import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  discardOperation,
  flushOutbox,
  listOperations,
  retryOperation,
  subscribeToOutbox,
  type OutboxOperation,
} from "./offline-outbox";
import { outboxSender } from "./offline-sender";

/** Il battito: chi resta acceso a lungo riprova da solo, senza aspettare un evento. */
const HEARTBEAT_MILLISECONDS = 30_000;

/**
 * Legge la coda e resta aggiornato, senza farla partire.
 *
 * Serve alle schermate che mostrano quello che il dispositivo tiene per se, per esempio
 * le richieste materiali dentro il loro elenco. A far partire la coda ci pensa un solo
 * posto in tutta l'app, cosi due schermate aperte non mandano niente due volte.
 */
export function useOutboxOperations(companyId: string | null, userId: string | null) {
  const [operations, setOperations] = useState<OutboxOperation[]>([]);
  const owner = useMemo(
    () => (companyId && userId ? { companyId, userId } : null),
    [companyId, userId],
  );

  const refresh = useCallback(async () => {
    if (!owner) { setOperations([]); return; }
    setOperations(await listOperations(owner));
  }, [owner]);

  useEffect(() => {
    if (!owner) { setOperations([]); return; }
    let alive = true;
    const unsubscribe = subscribeToOutbox(() => { if (alive) void refresh(); });
    void refresh();
    return () => { alive = false; unsubscribe(); };
  }, [owner, refresh]);

  const riprova = useCallback(async (id: string) => {
    if (!owner) return;
    await retryOperation(id, owner);
    await flushOutbox(owner, outboxSender);
    await refresh();
  }, [owner, refresh]);

  const elimina = useCallback(async (id: string) => {
    if (!owner) return;
    await discardOperation(id, owner);
    await refresh();
  }, [owner, refresh]);

  return { operations, riprova, elimina };
}

/**
 * Guarda la coda e la fa partire, senza che nessuno tocchi un bottone.
 *
 * Tre inneschi: l'apertura dell'app, l'evento `online` del sistema, e un battito periodico
 * per la rete che torna senza dirlo. Il quarto, il rientro sull'app dopo che era in ombra,
 * conta piu di quanto sembri su un telefono in cantiere.
 */
export function useOfflineOutbox(
  companyId: string | null,
  userId: string | null,
  onSynced?: () => void,
) {
  const [operations, setOperations] = useState<OutboxOperation[]>([]);
  const synced = useRef(onSynced);
  synced.current = onSynced;

  const owner = useMemo(
    () => (companyId && userId ? { companyId, userId } : null),
    [companyId, userId],
  );

  const refresh = useCallback(async () => {
    if (!owner) { setOperations([]); return; }
    setOperations(await listOperations(owner));
  }, [owner]);

  const synchronize = useCallback(async () => {
    if (!owner) return;
    const summary = await flushOutbox(owner, outboxSender);
    await refresh();
    // Quello che e partito adesso non e ancora negli elenchi a schermo: vanno riletti.
    if (summary.inviate > 0) synced.current?.();
  }, [owner, refresh]);

  useEffect(() => {
    if (!owner) { setOperations([]); return; }
    let alive = true;
    const unsubscribe = subscribeToOutbox(() => { if (alive) void refresh(); });
    const attempt = () => { void synchronize().catch(() => { /* si riprova al giro dopo */ }); };
    const onVisible = () => { if (document.visibilityState === "visible") attempt(); };

    void refresh();
    attempt();
    window.addEventListener("online", attempt);
    document.addEventListener("visibilitychange", onVisible);
    const heartbeat = window.setInterval(attempt, HEARTBEAT_MILLISECONDS);
    return () => {
      alive = false;
      unsubscribe();
      window.removeEventListener("online", attempt);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(heartbeat);
    };
  }, [owner, refresh, synchronize]);

  const riprova = useCallback(async (id: string) => {
    if (!owner) return;
    await retryOperation(id, owner);
    await synchronize();
  }, [owner, synchronize]);

  const elimina = useCallback(async (id: string) => {
    if (!owner) return;
    await discardOperation(id, owner);
    await refresh();
  }, [owner, refresh]);

  return {
    operations,
    inAttesa: operations.filter((operation) => operation.status === "pending"),
    segnalate: operations.filter((operation) => operation.status === "blocked"),
    sincronizza: synchronize,
    riprova,
    elimina,
  };
}
